import { formatSalary } from "../functions/salary-formatter";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";


const fetchJobDetails = async (jobId) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/jobs/job?job_id=${jobId}`);
  if (!response.data.data || !response.data.data.id) {
    return null;
  }
  const job = response.data?.data;
  const jobDetails = {
    id: job.id,
    company: job.company_name || '',
    title: job.title,
    location: job.location,
    salary: job.salary.min ? formatSalary(job.salary, job.jobType) : "Not Disclosed",
    role: job.role,
    jobStatus: job.jobStatus,
    jobType: job.jobType,
    jobDescription: job.jobDescription || '',
    jobDescriptionURL: job.jobDescriptionURL || '',
    logo: job.company_logo || '',
    status: job.jobStatus || '',
    createdAt: job.created_at || '',
    about: job.about || '',
  };
  return jobDetails;
};
const fetchJobs = async ({
  pageParam = 1,
  limit = 10,
  filters,
}) => {
  // Build query parameters for backend API
  const params = new URLSearchParams();
  params.append('page', pageParam.toString());
  params.append('limit', limit.toString());

  if (filters?.location) params.append('location', filters.location);
  if (filters?.role) params.append('role', filters.role);
  if (filters?.company) params.append('company', filters.company);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.filterType) params.append('filterType', filters.filterType);
  if (filters?.search) params.append('search', filters.search);

  if (filters?.jobType && Array.isArray(filters.jobType)) {
    filters.jobType.forEach(type => params.append('jobType', type));
  }
  const queryString = params.toString();
  const url = queryString
    ? `${process.env.REACT_APP_API_URL}/jobs/all?${queryString}`
    : `${process.env.REACT_APP_API_URL}/jobs/all`;

  const response = await axios.get(url);

  // Format salary on the frontend


  // Transform backend response to match frontend expectations
  const transformedJobs = response.data.data.jobs?.map((job) => ({
    id: job.id,
    company: job.company_name || '',
    title: job.title,
    location: job.location || "Remote",
    salary: job.salary,
    role: job.role,
    jobType: job.jobType,
    jobStatus: job.jobStatus,
    jobDescription: job.jobDescription || '',
    jobDescriptionURL: job.jobDescriptionURL || '',
    logo: job.company_logo || '',
    status: job.jobStatus || '',
    createdAt: job.created_at || '',
    about: job.about || '',
    mentor_role_id: job.mentor_role_id || '',
  }));

  return {
    data: transformedJobs || [],
    nextPage: response.data.data.pagination.hasNextPage ? pageParam + 1 : undefined,
    hasMore: response.data.data.pagination.hasNextPage,
    pagination: response.data.data.pagination,
  };
};


export function useJobs({
  limit = 10,
  enabled = true,
  filters,
}) {
  return useInfiniteQuery({
    queryKey: ['jobs', 'all', limit, filters],
    queryFn: ({ pageParam }) =>
      fetchJobs({
        pageParam,
        limit,
        filters,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    enabled,
  });
}

// Simple hook for basic jobs fetching (non-infinite)
export function useJobsList({
  enabled = true,
  filters,
}) {
  return useInfiniteQuery({
    queryKey: ['jobs', 'list', filters],
    queryFn: () =>
      fetchJobs({
        pageParam: 1,
        limit: 100, // Get all jobs for simple list
        filters,
      }),
    getNextPageParam: () => undefined, // No pagination for simple list
    initialPageParam: 1,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    enabled,
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      // Flatten all jobs from all pages
      jobs: data.pages.flatMap(page => page.data),
      // Include pagination info from the first page
      totalCount: data.pages[0]?.pagination?.totalCount || 0,
      totalPages: data.pages[0]?.pagination?.totalPages || 0,
    }),
  });
}


export function useJobDetails(jobId, initialJobDetails) {
  return useQuery({
    queryKey: ['job-details', jobId],
    queryFn: () => fetchJobDetails(jobId),
    enabled: !!jobId,
    staleTime: 30 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: initialJobDetails,
  });
}
